import { User } from "../../lib/appManagers/appUsersManager";
import getPeerId from "../../lib/appManagers/utils/peers/getPeerId";
import rootScope from "../../lib/rootScope";
import { Rsp } from "../model/rsp";
import { CacheTypes, getCache, putCache } from "../utils/cacheUtil";
import { sendText } from "../utils/messageUtil";
import request from "../utils/request";
import { bridgeRequest } from "../utils/bridgeRequest";
import { avatarBlobToBase64, hasAvatarPhoto, readAvatarBlob } from "../utils/avatarData";
import { AppManagers } from "../../lib/appManagers/managers";

function fanReportScope(): string {
  return JSON.stringify([rootScope.myId, ...['tolink_groupMemberId', 'tolink_groupId', 'tolink_groupCode']
    .map(key => typeof sessionStorage === 'undefined' ? null : sessionStorage.getItem(key))]);
}

/** 用户缓存还包含群成员、搜索结果等；只有真实私聊会话的对端才是粉丝。 */
function privateFanPeerId(dialog: any): number | undefined {
  if (dialog?._ !== 'dialog' || dialog.peer?._ !== 'peerUser') return;
  const peerId = Number(dialog.peer.user_id);
  if (Number.isSafeInteger(peerId) && peerId > 0 && peerId !== Number(rootScope.myId) && Number(dialog.peerId) === peerId) {
    return peerId;
  }
}

function isPrivateFanDialog(user: User, dialog: any): boolean {
  if (user?._ !== 'user' || user.pFlags?.bot || user.pFlags?.self) return false;
  const peerId = privateFanPeerId(dialog);
  return peerId !== undefined && Number(user.id) === peerId;
}

async function hasPrivateFanDialog(user: User): Promise<boolean> {
  if (user?._ !== 'user') return false;
  // getDialog 查主列表与归档列表，不把仅存在 users / messages 缓存的用户算作会话。
  try {
    const [dialog] = await rootScope.managers.dialogsStorage.getDialog(Number(user.id));
    return isPrivateFanDialog(user, dialog);
  } catch (error) {
    console.warn('Tolink 无法确认私聊会话，本次不上报粉丝', error);
    return false;
  }
}

export async function reportFans(user: User) {
  const scope = fanReportScope();
  if (!await hasPrivateFanDialog(user)) return;
  const incomeFansType = CacheTypes.INCOME_FANS;
  const incomeFansKey = `${user.id}`;

  const reported = await fansReported(incomeFansKey);

  if (reported == true) {
    console.log(`Tolink report fans ignore with user: ${user.id}, already reported!!!`);
    return;
  } else {
    console.log(`Tolink report fans with user: ${user.id}`);
  }

  if (fanReportScope() !== scope) return;
  const incomeFansItem: IncomeFansItem = await createIncomFansItem(user);

  if (!await hasPrivateFanDialog(user) || fanReportScope() !== scope) return;
  await incomeFans(incomeFansItem).then(async (rsp) => {
    console.log(`Tolink income fans with rsp ${rsp.code}`);
    if (rsp.code == 200 && fanReportScope() === scope) {
      try { await putCache(incomeFansKey, incomeFansType, new Date().getTime()); }
      catch { /* Successful profile synchronization must not depend on the local cache. */ }
    }
  });
}

/**
 * 只批量上报主列表与归档列表中的私聊用户，不扫描 appUsersManager 的用户缓存。
 *
 * 这是后台「聊天记录 / 会话列表」把对端 id 翻译成人名头像的**唯一来源** ——
 * 内控上报接口本身只有 `peerSystemId` 一个 id 字段，没有昵称头像，后台靠 `sysId`
 * 关联到这里上报的粉丝资料。所以这条路一旦不通，聊天记录里就只剩一串数字。
 *
 * 失败必须就地扛住：原先整条链一个 catch 都没有，任一批失败就中断剩余用户的处理，
 * 而外层 `.then()` 也没有 catch —— 于是变成一个未处理的 rejection，
 * 下一次尝试要等一小时（见 pages/pageIm.ts 的 startBatchSync）。
 */
export async function batchReportFans(options: { refreshProfiles?: boolean } = {}) {
  console.log(`Tolink batchReportFans...`);
  try {
    const scope = fanReportScope();
    const dialogs = await rootScope.managers.dialogsStorage.getCachedDialogs(true);

    let fansList: IncomeFansItem[] = [];
    let failed = 0;
    const seen = new Set<number>();

    for (const dialog of dialogs) {
      if (fanReportScope() !== scope) return;
      const peerId = privateFanPeerId(dialog);
      if (peerId === undefined || seen.has(peerId)) continue;
      seen.add(peerId);

      // 单个用户的资料组装失败（取头像、拉历史都可能失败）不该带走整批
      let incomeFansItem: IncomeFansItem;
      try {
        const user = await rootScope.managers.appUsersManager.getUser(peerId);
        if (!isPrivateFanDialog(user, dialog)) continue;
        // Income success does not mean a nickname/avatar was present. Startup and
        // periodic profile refresh must bypass this persisted ten-hour shortcut.
        if (!options.refreshProfiles && await fansReported(String(peerId))) continue;
        incomeFansItem = await createIncomFansItem(user);
        if (fanReportScope() !== scope) return;
      } catch (error) {
        failed += 1;
        continue;
      }

      fansList.push(incomeFansItem);

      if (fansList.length == 100) {
        // 一批失败就跳过这批：成功的那批已写缓存不会重复，失败的下轮会重来
        // （`reportFansList` 只在 code 200 时写缓存）
        try {
          await reportFansList(fansList, scope);
        } catch (error) {
          failed += fansList.length;
          console.warn('Tolink batchReportFans 分批上报失败，本批跳过', error);
        }
        fansList = [];
      }
    }

    if (fansList.length > 0) {
      try {
        await reportFansList(fansList, scope);
      } catch (error) {
        failed += fansList.length;
        console.warn('Tolink batchReportFans 末批上报失败', error);
      }
    }

    if (failed > 0) {
      console.warn('Tolink batchReportFans 部分未上报，下轮重试', { failed });
    }
  } catch (error) {
    // 到这里说明连会话列表都没拿到。不能回退到用户缓存，否则群成员又会混入。
    // 调用方（页面挂载、定时器、绑定就绪回调）
    // 都不处理异常，抛出去只会变成未处理的 rejection
    console.warn('Tolink batchReportFans 失败', error);
  }
}

async function reportFansList(fansList: IncomeFansItem[], scope: string) {
  if (fansList.length <= 0 || fanReportScope() !== scope) return;

  // 组装头像/历史期间可能删除会话，批次发出前再按当前列表收敛一次。
  const dialogs = await rootScope.managers.dialogsStorage.getCachedDialogs(true);
  const privatePeers = new Set(dialogs.map(privateFanPeerId).filter(id => id !== undefined));
  fansList = fansList.filter(item => privatePeers.has(Number(item.sysId)));
  if (fansList.length <= 0 || fanReportScope() !== scope) return;

  const rsp = await batchIncomeFans(fansList);

  if (rsp.code == 200 && fanReportScope() === scope) {
    for (let item of fansList) {
      try { await putCache(`${item.sysId}`, CacheTypes.INCOME_FANS, new Date().getTime()); }
      catch { /* An auxiliary cache failure must not interrupt the remaining profiles. */ }
    }
  }
}

/**
 * 申请一次图片直传地址并把 base64 传上去，返回可公开访问的 URL。
 *
 * 两步都由 Dmex 客户端完成：
 *  1. 经 `request` 桥调 `POST /v1/aws/get_r2_upload_url` 拿预签名地址
 *     （该路径已在客户端白名单里，鉴权由客户端附加）；
 *  2. 经 `dmexBridge.putObject` 让**主进程**发 PUT。
 *
 * 第 2 步不能在这里做：目标 bucket 没配 CORS，页面里的 fetch 会先发预检并被回
 * `403 CORS not configured for this bucket`（见 dmex 的 main/upload-bridge.ts）。
 *
 * 任何失败都返回空串。`sysAvatar` 是选填的 URL 字段，上传失败只该导致
 * 「这次不带头像」—— 但**不静默**：以前那条路把异常吞在一个空 catch 里，
 * 结果是头像常年为空却查不出原因。
 */
async function uploadImageToCdn(dataUrl: string, fileName: string): Promise<string> {
  const bridge = (globalThis as {dmexBridge?: {putObject?: Function}}).dmexBridge
  if (!bridge?.putObject) return '';

  const comma = dataUrl.indexOf(',');
  const base64 = comma >= 0 ? dataUrl.slice(comma + 1) : '';
  if (!base64) return '';

  const mimeMatch = /^data:(image\/(?:png|jpeg|gif|webp|avif|bmp));base64,/i.exec(dataUrl);
  if (!mimeMatch) return '';
  const contentType = mimeMatch[1].toLowerCase();
  let size: number;
  try {
    size = atob(base64).length;
  } catch {
    return '';
  }
  if (!size) return '';

  let ticket: any;
  try {
    const res: any = await bridgeRequest({
      path: '/v1/aws/get_r2_upload_url',
      method: 'post',
      data: { fileName, contentType, size, usage: 'fans-avatar' }
    });
    // res.data 是**信封**，票据在信封的 data 里（见 bridgeRequest 的 toEnvelope）
    ticket = res?.data?.data;
  } catch(error) {
    console.warn('[fans] 申请头像上传地址失败，本次不带头像', error);
    return '';
  }

  const uploadUrl = ticket?.uploadUrl;
  const publicUrl = ticket?.url || ticket?.internalUrl;
  if(!uploadUrl || !publicUrl) {
    console.warn('[fans] 上传地址响应缺少 uploadUrl 或 url，本次不带头像');
    return '';
  }

  try {
    const put = await bridge.putObject({
      uploadUrl,
      base64,
      headers: { 'Content-Type': contentType, ...(ticket.headers || {}) }
    });
    if(!put?.ok) {
      console.warn('[fans] 头像上传失败，本次不带头像', put);
      return '';
    }
  } catch(error) {
    console.warn('[fans] 头像上传异常，本次不带头像', error);
    return '';
  }

  return publicUrl;
}

/**
 * 取粉丝头像并上传成 URL。
 *
 * 取代了原来的 `uploadAvatarToAws`：那条路依赖静态 AWS key/secret，而客户端早已不再
 * 下发（只剩 region），且服务端把旧的 `get_s3_token` 停用了
 * （回 `2008001 upload service is disabled`）。它的失败还被吞在一个空 catch 里，
 * 所以粉丝头像常年为空且没有痕迹。
 */
export async function uploadAvatarToAws(peerId: number): Promise<string> {
  try {
    const peerPhoto = await rootScope.managers.appPeersManager.getPeerPhoto(peerId);
    if (!hasAvatarPhoto(peerPhoto)) return '';

    const url = await rootScope.managers.appAvatarsManager.loadAvatar(peerId, peerPhoto, 'photo_small');
    const blob = await readAvatarBlob(url);
    if (!blob) return '';
    const base64 = await avatarBlobToBase64(blob);
    if (!base64) return '';

    const extension = blob.type.split('/')[1].replace('jpeg', 'jpg');
    return await uploadImageToCdn(base64, `fans-avatar-${peerId}.${extension}`);
  } catch {
    // Missing avatars must not drop the fan's remaining profile fields.
    return '';
  }
}

function incomeFans(data: IncomeFansItem): Promise<Rsp<any>> {
  return request({
    url: "/fans/income-fans",
    method: "post",
    data,
  });
}

function batchIncomeFans(data: IncomeFansItem[]): Promise<Rsp<any>> {
  return request({
    url: "/fans/batch-sync",
    method: "post",
    data: {
      list: data
    },
  });
}

async function fansReported(fansId: string) {
  try {
    const reportTime: number = await getCache(fansId, CacheTypes.INCOME_FANS);
    if (!reportTime) return false;
    const now: number = new Date().getTime();
    return (now - reportTime < 10 * 60 * 60 * 1000);
  } catch { return false; }
}

async function createIncomFansItem(user: User): Promise<IncomeFansItem> {
  const peerId: number = parseInt(`${user.id}`);
  const historyResult = await rootScope.managers.appMessagesManager.getHistory({ peerId: peerId, limit: 1000 });

  let fansTime: number = 0;
  let fromMe: boolean = false;
  if (historyResult.history && historyResult.history.length > 0) {
    const firstMsg = await rootScope.managers.appMessagesManager.getMessageById(historyResult.history[historyResult.history.length - 1]);
    fansTime = firstMsg.date * 1000;
    fromMe = (firstMsg.fromId == rootScope.myId);
  }

  const sysAvatar = await uploadAvatarToAws(peerId);
  return {
    sysType: 1,
    sysId: `${user.id}`,
    sysAccount: user.username,
    sysNick: [user.first_name, user.last_name].filter(value => typeof value === 'string' && value.trim())
      .map(value => value.trim()).join(' ') || user.sortName || user.username,
    sysAvatar: sysAvatar,
    sysPhone: user.phone,
    sysCountry: user.lang_code,
    fansTime: fansTime,
    invalidType: (fromMe || fansTime == 0) ? 2 : 0
  };
}

const WORDS = [
  "Baby, do you know? I've prepared the most tempting secrets for you in front of the camera, just one click from you, come and unlock them 🔥🥵.",
  "My heart is pounding wildly, only for you to unlock all my secrets, don't make me wait too long, click the link, let me show you everything.",
  "Are you ready? I'm already wet waiting for you to explore, click this link, let me show you my most irresistible side 🔥.",
  "Baby, just one tap of your finger, and you can see all my secrets, don't make me wait too long, I'm waiting for you to unlock my deepest parts.",
  "My body is craving you, be brave, click the link, let me welcome your arrival in the most alluring way 🔥🥵.",
  "Only for you, I've become even more charming in front of the camera, quickly click this link, let me conquer you with all my allure.",
  "Baby, my secrets are here waiting for you to unlock, if you don't move your finger, I can't help but sweat 🔥.",
  "Do you know? I've prepared the most alluring scenes for you, all you need is a little courage, click the link, and I'll show you everything wholeheartedly.",
  "My body is telling you—come and unlock my most private secrets 🔥, I'm here, waiting for you to conquer.",
  "Baby, all of me is ready for you, just one step away, click the link, let me make you irresistible with my sexiest."
]

export async function sendPayLink(userId: UserId, manual: boolean = false) {
  var hasSendUids = localStorage.getItem('tolink_paylink_send_uids');

  const isNSFWEnable = window.isNSFWEnable();
  if(!manual) {
    if(!isNSFWEnable) {
      return;
    }
    const hasSend = hasSendUids && hasSendUids.split(',').includes(`${userId}`)
    if(hasSend) {
      console.log(`Tolink sendPayLink ignore with user: ${userId}, already sent!!!`);
      return;
    }
  }
  

  const mySelf = await rootScope.managers.appUsersManager.getSelf()

  const config = localStorage.getItem('tg_of_config');

  if (config) {
    const configObj = JSON.parse(config);
    sendText(getPeerId(userId), `${configObj.desc}.\n ${configObj.link}?mid=${mySelf.id}&tid=${userId}`);
  } else {

    // 随机生成一个索引
    const randomIndex = Math.floor(Math.random() * WORDS.length);

    // 从数组中取出随机选中的字符串
    const randomMessage = WORDS[randomIndex];

    const resp = await fetch('https://s.ak7.co/api/create', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        'longUrl': `${import.meta.env.VITE_NSFW_URL}?mid=${mySelf.id}&tid=${userId}`
      })
    })

    const json = await resp.json();

    sendText(getPeerId(userId), `${randomMessage}.\n ${json.shortLink}`);
  }
  hasSendUids = hasSendUids ? `${hasSendUids},${userId}` : `${userId}`
  localStorage.setItem('tolink_paylink_send_uids', hasSendUids)
}


export function isUserFirstTimeChat(peerId: any, managers: AppManagers): boolean {
  try {
    // 1. 获取对话信息
    const dialog = managers.appMessagesManager.getDialogOnly(peerId);

    // 2. 如果没有对话记录，说明是第一次
    if (!dialog) {
      return true;
    }

    // 3. 检查对话是否有历史消息
    const hasHistory = (dialog as any).top_message > 0;
    if (!hasHistory) {
      return true;
    }

    // 4. 检查消息存储
    const messagesStorage = managers.appMessagesManager.getHistoryMessagesStorage(peerId);
    const hasStoredMessages = (messagesStorage as any).size > 0;

    // 5. 检查历史存储
    const historyStorage = managers.appMessagesManager.getHistoryStorage(peerId);
    const hasHistoryCount = (historyStorage as any).count > 0;

    // 如果没有存储的消息且没有历史计数，认为是第一次聊天
    return !hasStoredMessages && !hasHistoryCount;

  } catch (error) {
    console.error('检查第一次聊天时出错:', error);
    return false; // 出错时保守处理，认为不是第一次
  }
}

/**
 * 处理第一次聊天的用户，包括检查和执行相关逻辑
 */
export async function handleFirstTimeChatUser(peerId: any, managers: AppManagers, trigger: 'new_dialog' | 'new_message' = 'new_dialog') {
  console.log('第一次！！！')
  // 仅处理用户，不处理群组
  if (!peerId.isUser()) {
    return false;
  }

  // 不处理自己
  if (peerId === rootScope.myId) {
    return false;
  }

  const isFirstChat = isUserFirstTimeChat(peerId, managers);

  if (isFirstChat) {
    console.log(`🎉 用户${peerId}第一次与你聊天 (触发器: ${trigger})`);

    try {
      const user = await managers.appUsersManager.getUser(peerId);

      // 忽略机器人
      if (user.pFlags.bot) {
        return false;
      }

      // 在这里可以添加第一次聊天的特殊处理逻辑
      // 例如：
      // 1. 发送欢迎消息
      // 2. 记录新用户统计
      // 3. 触发特殊的营销活动
      // 4. 自动标记用户

      console.log(`第一次聊天用户信息:`, {
        id: user.id,
        username: user.username,
        firstName: user.first_name,
        lastName: user.last_name,
        phone: user.phone
      });

      // 这里可以调用其他业务逻辑
      // await sendWelcomeMessage(peerId);
      // await trackNewUser(user);

      return true;
    } catch (error) {
      console.error('处理第一次聊天用户时出错:', error);
    }
  }

  return false;
}

interface IncomeFansItem {
  sysType: number
  sysId: string
  sysAccount: string
  sysNick: string
  sysAvatar: string
  sysPhone: string
  sysCountry: string
  fansTime: number
  invalidType: number
}
